import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AvailabilityStatus = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState({
    currentStatus: 'away',
    nextAvailable: '',
    responseTime: {
      email: '< 24 hours',
      phone: 'Next business day',
      urgent: '< 4 hours'
    },
    workingHours: {
      weekdays: '9:30 AM - 6:30 PM IST',
      weekends: 'Limited availability',
      timezone: 'Indian Standard Time (UTC+5:30)'
    }
  });

  useEffect(() => {
    const timer = setInterval(() => {
      const nowIST = new Date(
        new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' })
      );
      setCurrentTime(nowIST);

      const day = nowIST.getDay(); // Sunday = 0, Saturday = 6
      const hour = nowIST.getHours();
      const minutes = nowIST.getMinutes();

      const isWeekday = day >= 1 && day <= 5;
      const isWithinBusinessHours =
        (hour > 9 && hour < 18) ||
        (hour === 9 && minutes >= 30) ||
        (hour === 18 && minutes === 0);

      const inWorkingHours = isWeekday && isWithinBusinessHours;

      let nextAvailableMessage = null;
      if (!inWorkingHours) {
        if (isWeekday && (hour < 9 || (hour === 9 && minutes < 30))) {
          nextAvailableMessage = 'Today at 9:30 AM IST';
        } else if (day === 5 && hour >= 18) {
          nextAvailableMessage = 'Monday at 9:30 AM IST';
        } else if (day === 6) {
          nextAvailableMessage = 'Monday at 9:30 AM IST';
        } else if (day === 0) {
          nextAvailableMessage = 'Monday at 9:30 AM IST';
        } else {
          nextAvailableMessage = 'Tomorrow at 9:30 AM IST';
        }
      }

      setAvailabilityData({
        currentStatus: inWorkingHours ? 'available' : 'away',
        nextAvailable: inWorkingHours ? null : nextAvailableMessage,
        responseTime: {
          email: '< 24 hours',
          phone: inWorkingHours ? 'Immediate' : 'Next business day',
          urgent: '< 4 hours'
        },
        workingHours: {
          weekdays: '9:30 AM - 6:30 PM IST',
          weekends: 'Limited availability',
          timezone: 'Indian Standard Time (UTC+5:30)'
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const upcomingAvailability = [
    {
      date: 'This Week',
      status: 'Limited slots available',
      icon: 'Calendar',
      color: 'warning'
    },
    {
      date: 'Next Week',
      status: 'Good availability',
      icon: 'CheckCircle',
      color: 'success'
    },
    {
      date: 'This Month',
      status: 'Booking for new projects',
      icon: 'Clock',
      color: 'accent'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'busy':
        return 'warning';
      case 'away':
        return 'error';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return 'CheckCircle';
      case 'busy':
        return 'Clock';
      case 'away':
        return 'Moon';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-50"></div>
      <div className="relative z-10 mb-6">
        <h2 className="font-heading font-semibold text-2xl text-text-primary mb-2">
          Current Availability
        </h2>
        <p className="text-text-secondary">Real-time status and response expectations</p>
      </div>

      {/* Current Status */}
      <div className="bg-white/5 border border-white/10 rounded-lg p-4 mb-6 relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${availabilityData.currentStatus === 'available' ? 'bg-success' : 'bg-error'} animate-pulse`}></div>
            <span className="font-medium text-text-primary capitalize">
              {availabilityData.currentStatus === 'available' ? 'Available Now' : 'Currently Away'}
            </span>
          </div>
          <div className="text-sm text-text-muted">
            {currentTime.toLocaleTimeString('en-IN', {
              timeZone: 'Asia/Kolkata',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </div>
        </div>

        {availabilityData.nextAvailable && (
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="Clock" size={16} />
            <span>Next available: {availabilityData.nextAvailable}</span>
          </div>
        )}
      </div>

      {/* Response Times */}
      <div className="mb-6 relative z-10">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">Response Times</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <div className="flex items-center space-x-3">
              <Icon name="Mail" size={18} className="text-accent-light" />
              <span className="text-text-secondary">Email inquiries</span>
            </div>
            <span className="font-medium text-text-primary">{availabilityData.responseTime.email}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <div className="flex items-center space-x-3">
              <Icon name="Phone" size={18} className="text-accent-light" />
              <span className="text-text-secondary">Phone calls</span>
            </div>
            <span className="font-medium text-text-primary">{availabilityData.responseTime.phone}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-3">
              <Icon name="AlertCircle" size={18} className="text-error-light" />
              <span className="text-text-secondary">Urgent matters</span>
            </div>
            <span className="font-medium text-text-primary">{availabilityData.responseTime.urgent}</span>
          </div>
        </div>
      </div>

      {/* Working Hours */}
      <div className="mb-6 relative z-10">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4 flex items-center space-x-2">
          <Icon name="Clock" size={18} className="text-accent-light" />
          <span>Working Hours (IST)</span>
        </h3>
        <div className="space-y-2 text-text-secondary text-sm">
          <div className="flex items-center justify-between">
            <span>Monday - Friday</span>
            <span className="font-medium text-text-primary">9:30 AM – 6:30 PM</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Weekends</span>
            <span className="font-medium text-text-primary">Limited availability</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Timezone</span>
            <span className="text-text-primary">Indian Standard Time (UTC+5:30)</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Current IST Time</span>
            <span className="font-medium">
              {currentTime.toLocaleTimeString('en-IN', {
                timeZone: 'Asia/Kolkata',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AvailabilityStatus;
